import { FormGenericContainer } from '@go-client/components/FormGenericContainer';
import React, { useState } from 'react';
import {
  BubbleBadge,
  BubbleButton,
  BubbleFormGroup,
  BubbleInput,
  BubbleSelect,
} from '@go-client/components/BubbleUI';
import { GroupBookingFormWorkflow, validateResources } from '.';
import { hashStringToColor } from '@go-shared/lib/utils';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import { groupResourceClient } from '@go-client/lib/api/v2/groupResource';
import { GroupsResourceMode } from '../GroupResourceForm';

interface Form {
  name: string;
  price: number;
  resourceType: string;
}

interface GroupResourceFormPageProps {
  resourceTypes: string[];
  onCreated: () => void;
  preloadData?: { id: string; name: string; price: number; resourceType: string };
  mode: GroupsResourceMode;
  onClose: () => void;
}

function GroupResourceFormPage(props: GroupResourceFormPageProps) {
  const { resourceTypes, onCreated, preloadData, mode, onClose } = props;

  const [form, setForm] = useState<Form>({
    name: preloadData?.name ?? '',
    price: preloadData?.price ?? 0.0,
    resourceType: preloadData?.resourceType ?? resourceTypes[0],
  });
  const isFormValid = form.name.trim() !== '' && form.price >= 0;
  const { activeProperty } = useActiveProperty();

  async function handleOnSubmit() {
    const { error: isDuplicated } = await validateResources([form]);
    if (isDuplicated) return;

    const rq = {
      ...form,
      brand: activeProperty.brand._id,
      properties: [activeProperty._id],
      archived: false,
      source: activeProperty.brand._id,
    };

    switch (props.mode.type) {
      case 'clone':
      case 'create':
        await groupResourceClient.create({
          body: rq,
          params: { brandId: activeProperty.brand._id },
        });
        break;
      case 'edit':
        if (!preloadData) {
          throw new Error('preloadData is required for editing a resource.');
        }
        await groupResourceClient.update({
          body: form,
          params: { resourceId: preloadData.id },
        });
    }

    onCreated();
    onClose();
  }

  return (
    <div className="tflf-3">
      <FormGenericContainer
        footer={() => (
          <>
            <div className="text-slate-dark"></div>
            <BubbleButton
              className="ml-auto"
              variant="navy"
              disabled={!isFormValid}
              onClick={handleOnSubmit}
            >
              {mode.type === 'edit' ? 'Edit Resource' : 'Create New Resource'}
            </BubbleButton>
          </>
        )}
      >
        <div className="flex items-start mb-5">
          <div>
            <div className="flex items-center">
              <h1 className="text-lg text-slate-dark font-semibold">Create New Group Resource</h1>
              <BubbleBadge color={hashStringToColor(activeProperty.name)} className="ml-3">
                {activeProperty.name}
              </BubbleBadge>
            </div>
            <h2 className="text-2xl text-navy">1. Enter Details</h2>
          </div>
          <div className="ml-auto" />

          <BubbleButton variant="navy" inverse onClick={onClose} icon="xmark-large" iconSize="xl" />
        </div>
        <div className="grid grid-cols-2 gap-4 items-center">
          <BubbleFormGroup label="Resource Name">
            <BubbleInput
              type="text"
              block
              value={form.name}
              onValue={val => setForm({ ...form, name: val })}
            />
          </BubbleFormGroup>
          <BubbleFormGroup label="Price">
            <BubbleInput
              type="number"
              step={0.01}
              min={0}
              block
              value={form.price}
              onValue={(val: number) => {
                setForm({ ...form, price: val });
              }}
            />
          </BubbleFormGroup>
          <BubbleFormGroup label="Type">
            <BubbleSelect
              block
              allowUndefined={false}
              options={resourceTypes}
              labelExtractor={type => type}
              value={form.resourceType}
              onValue={val => {
                if (val) setForm({ ...form, resourceType: val });
              }}
            />
          </BubbleFormGroup>
        </div>
      </FormGenericContainer>
    </div>
  );
}

export default function buildCreateGroupResourceWorkflow(
  props: GroupResourceFormPageProps
): GroupBookingFormWorkflow {
  const { resourceTypes, onCreated, preloadData, mode, onClose } = props;
  const prependNode = (
    <GroupResourceFormPage
      mode={mode}
      resourceTypes={resourceTypes}
      onCreated={onCreated}
      preloadData={preloadData}
      onClose={onClose}
    />
  );

  return { prependNode };
}
